// Seleção (DESIGN §5.3): select nativo, segmentado, radio em lista, alternador (switch) e caixa (checkbox).
// Seleção nunca usa vermelho.
import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { classesCampo, MolduraCampo } from "./Campo";
import { Icones } from "./icones";

export interface OpcaoSelecao<V extends string = string> {
  valor: V;
  rotulo: string;
}

export interface PropsSelecao extends SelectHTMLAttributes<HTMLSelectElement> {
  rotulo?: ReactNode;
  ajuda?: ReactNode;
  erro?: ReactNode;
  dentroPainel?: boolean;
  opcoes: OpcaoSelecao[];
  /** Grupos (`<optgroup>`) depois das opções soltas (ex.: "Do turno do aluno (noite)"). */
  grupos?: { rotulo: string; opcoes: OpcaoSelecao[] }[];
  /** Primeira opção vazia ("Selecione"). */
  vazio?: string;
}

/** `<select>` nativo com cara de campo — no celular o picker do sistema é o melhor. */
export const Selecao = forwardRef<HTMLSelectElement, PropsSelecao>(function Selecao(
  { rotulo, ajuda, erro, dentroPainel, opcoes, grupos, vazio, className, id, ...resto },
  ref,
) {
  const autoId = useId();
  const idCampo = id ?? autoId;
  return (
    <MolduraCampo id={idCampo} rotulo={rotulo} ajuda={ajuda} erro={erro}>
      <div className="relative">
        <select
          ref={ref}
          id={idCampo}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? `${idCampo}-erro` : ajuda ? `${idCampo}-ajuda` : undefined}
          className={cn(classesCampo({ erro: !!erro, dentroPainel }), "appearance-none pr-12", className)}
          {...resto}
        >
          {vazio !== undefined && <option value="">{vazio}</option>}
          {opcoes.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
          {grupos?.map((g) => (
            <optgroup key={g.rotulo} label={g.rotulo}>
              {g.opcoes.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <Icones.expandir
          size={20}
          aria-hidden
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-texto-3"
        />
      </div>
    </MolduraCampo>
  );
});

export interface PropsSegmentado<V extends string> {
  rotulo: string;
  /**
   * `desabilitada`: aparece apagada (opacidade 40) e fora do teclado; diga o motivo ao lado, não em tooltip.
   * `rotuloCurto`: texto abaixo do `lg`, quando o longo não cabe numa linha (ex.: "Iguais" no celular).
   * `contagem`: número depois do texto em negrito largo ("Do EVO **41**", mockup Treinos › Modelos, 30/09).
   */
  opcoes: (OpcaoSelecao<V> & { desabilitada?: boolean; rotuloCurto?: string; contagem?: number })[];
  valor: V;
  aoMudar: (v: V) => void;
  /** Partes de largura igual (pranchas Carteira e Novo aluno: `repeat(n, 1fr)`); padrão: pela largura do texto. */
  partesIguais?: boolean;
  className?: string;
}

/** Controle segmentado (2 a 4 opções curtas). role="radiogroup", setas do teclado movem. */
export function Segmentado<V extends string>({
  rotulo,
  opcoes,
  valor,
  aoMudar,
  partesIguais = false,
  className,
}: PropsSegmentado<V>) {
  const mover = (delta: number) => {
    const ativas = opcoes.filter((o) => !o.desabilitada);
    const i = ativas.findIndex((o) => o.valor === valor);
    const prox = ativas[(i + delta + ativas.length) % ativas.length];
    if (prox) aoMudar(prox.valor);
  };
  return (
    <div
      role="radiogroup"
      aria-label={rotulo}
      className={cn(
        "grid h-11 grid-flow-col gap-1 rounded-controle bg-recuo p-1",
        partesIguais && "auto-cols-fr",
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          mover(1);
        }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          mover(-1);
        }
      }}
    >
      {opcoes.map((o, i) => {
        const ativo = o.valor === valor;
        // Nenhuma escolhida (campo opcional): o primeiro recebe o Tab.
        const focavel = ativo || (i === 0 && !opcoes.some((x) => x.valor === valor));
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ativo}
            tabIndex={focavel && !o.desabilitada ? 0 : -1}
            disabled={o.desabilitada}
            aria-disabled={o.desabilitada || undefined}
            onClick={() => aoMudar(o.valor)}
            className={cn(
              "rounded-[10px] px-3 text-apoio transition-colors disabled:opacity-40",
              partesIguais && "min-w-0 px-1 whitespace-nowrap",
              ativo
                ? "bg-painel font-semibold text-tinta shadow-anel"
                : "text-texto-2 enabled:hover:text-tinta",
            )}
          >
            {o.rotuloCurto ? (
              <>
                <span className="lg:hidden">{o.rotuloCurto}</span>
                <span className="max-lg:hidden">{o.rotulo}</span>
              </>
            ) : (
              o.rotulo
            )}
            {o.contagem !== undefined && (
              <b className={cn("ml-[5px] font-semibold placar", ativo ? "text-tinta" : "text-texto-3")}>
                {o.contagem}
              </b>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface OpcaoRadio<V extends string = string> extends OpcaoSelecao<V> {
  descricao?: ReactNode;
  /** Conteúdo à direita (ex.: prévia "Lucas F."). */
  extra?: ReactNode;
}

export interface PropsListaRadio<V extends string> {
  rotulo: string;
  /** Esconde o rótulo visualmente (quando há um h2 acima). */
  rotuloOculto?: boolean;
  nome?: string;
  opcoes: OpcaoRadio<V>[];
  valor: V | null;
  aoMudar: (v: V) => void;
  className?: string;
}

/** Radio em lista: linha inteira clicável, bolinha 22 px, selecionada com anel tinta (onboarding, privacidade). */
export function ListaRadio<V extends string>({
  rotulo,
  rotuloOculto,
  nome,
  opcoes,
  valor,
  aoMudar,
  className,
}: PropsListaRadio<V>) {
  const autoNome = useId();
  return (
    <fieldset className={cn("flex flex-col gap-2", className)}>
      <legend className={cn(rotuloOculto ? "sr-only" : "mb-2 text-apoio font-medium text-tinta")}>
        {rotulo}
      </legend>
      {opcoes.map((o) => {
        const ativo = o.valor === valor;
        return (
          <label
            key={o.valor}
            className={cn(
              "flex min-h-14 cursor-pointer items-center gap-3 rounded-card bg-painel px-4 py-3 shadow-anel",
              ativo && "ring-2 ring-tinta",
              "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-foco has-[:focus-visible]:outline-offset-2",
            )}
          >
            <input
              type="radio"
              name={nome ?? autoNome}
              value={o.valor}
              checked={ativo}
              onChange={() => aoMudar(o.valor)}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className={cn(
                "grid size-[22px] shrink-0 place-items-center rounded-full border-2",
                ativo ? "border-tinta bg-tinta" : "border-fio-controle",
              )}
            >
              {ativo && <span className="size-2 rounded-full bg-painel" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-titulo text-tinta">{o.rotulo}</span>
              {o.descricao && <span className="text-apoio text-texto-2">{o.descricao}</span>}
            </span>
            {o.extra && <span className="text-apoio text-texto-3">{o.extra}</span>}
          </label>
        );
      })}
    </fieldset>
  );
}

export interface PropsAlternador {
  rotulo: ReactNode;
  descricao?: ReactNode;
  ligado: boolean;
  aoMudar: (ligado: boolean) => void;
  desabilitado?: boolean;
  /**
   * Dentro de um bloco de formulário (gaveta): rótulo 14/600, descrição 12 em texto-3 e o trilho alinhado ao
   * topo (mockup Equipe › Atendimento, 28/09).
   */
  compacto?: boolean;
  className?: string;
}

/** Switch: trilho 52×32, ligado em tinta; linha inteira min-h-14 clicável. role="switch". */
export function Alternador({
  rotulo,
  descricao,
  ligado,
  aoMudar,
  desabilitado,
  compacto,
  className,
}: PropsAlternador) {
  const id = useId();
  return (
    <div
      className={cn(
        "flex min-h-14",
        compacto ? "items-start justify-between gap-4" : "items-center gap-3 py-2",
        desabilitado && "opacity-50",
        className,
      )}
    >
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer flex-col">
        <span className={compacto ? "text-apoio font-semibold text-tinta" : "text-titulo text-tinta"}>{rotulo}</span>
        {descricao && (
          <span className={compacto ? "mt-1 max-w-[300px] text-rotulo text-texto-3" : "text-apoio text-texto-2"}>
            {descricao}
          </span>
        )}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={ligado}
        disabled={desabilitado}
        onClick={() => aoMudar(!ligado)}
        className={cn(
          "relative h-8 w-[52px] shrink-0 rounded-full transition-colors duration-(--gs-dur-estado)",
          compacto && "mt-0.5",
          ligado ? "bg-tinta" : "bg-fio-controle",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute top-1 left-1 size-6 rounded-full bg-painel transition-transform duration-(--gs-dur-estado) ease-gs",
            ligado && "translate-x-5",
          )}
        />
      </button>
    </div>
  );
}

export interface PropsCaixa {
  rotulo: ReactNode;
  marcado: boolean;
  aoMudar: (marcado: boolean) => void;
  /** Esconde o texto (tabela com seleção em lote); continua acessível. */
  rotuloOculto?: boolean;
  desabilitado?: boolean;
  className?: string;
}

/** Checkbox 24 px com área de toque 48. */
export function Caixa({ rotulo, marcado, aoMudar, rotuloOculto, desabilitado, className }: PropsCaixa) {
  return (
    <label
      className={cn(
        "inline-flex min-h-toque cursor-pointer items-center gap-3",
        desabilitado && "opacity-50",
        className,
      )}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={marcado}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span
        aria-hidden
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded-[6px] border-2",
          "peer-focus-visible:outline-2 peer-focus-visible:outline-foco peer-focus-visible:outline-offset-2",
          marcado ? "border-tinta bg-tinta text-sobre-tinta" : "border-fio-controle bg-painel",
        )}
      >
        {marcado && <Icones.confirmar size={16} strokeWidth={3} />}
      </span>
      <span className={cn(rotuloOculto ? "sr-only" : "text-corpo text-tinta")}>{rotulo}</span>
    </label>
  );
}
